import React, { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { getUserProfile } from "../../services/userService";
import { useAnalytics } from "../../hooks/useAnalytics";
import { useTranslation } from "../../hooks/useTranslation";
import { useScope } from "../../hooks/useScope";
import "./UserMenu.css";

const UserMenu = () => {
  const { currentUser, signOut } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const dropdownRef = useRef(null);
  const history = useHistory();
  const analytics = useAnalytics();
  const t = useTranslation();
  const { isClientView } = useScope();

  // Load user profile from Firestore
  useEffect(() => {
    const loadUserProfile = async () => {
      if (currentUser) {
        try {
          const profile = await getUserProfile(currentUser.uid);
          setUserProfile(profile);
        } catch (error) {
          console.error("Error loading user profile:", error);
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };

    loadUserProfile();
  }, [currentUser]);

  // Refresh profile when it's updated (listen for custom event)
  useEffect(() => {
    const handleProfileUpdate = async () => {
      if (currentUser) {
        try {
          const profile = await getUserProfile(currentUser.uid);
          setUserProfile(profile);
        } catch (error) {
          console.error("Error refreshing user profile:", error);
        }
      }
    };

    window.addEventListener("userProfileUpdated", handleProfileUpdate);
    return () => {
      window.removeEventListener("userProfileUpdated", handleProfileUpdate);
    };
  }, [currentUser]);

  // Refresh profile when dropdown opens (in case it was updated elsewhere)
  useEffect(() => {
    if (isOpen && currentUser) {
      const refreshProfile = async () => {
        try {
          const profile = await getUserProfile(currentUser.uid);
          setUserProfile(profile);
        } catch (error) {
          console.error("Error refreshing user profile:", error);
        }
      };
      refreshProfile();
    }
  }, [isOpen, currentUser]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Get user display info
  const getUserDisplayName = () => {
    if (userProfile?.firstName && userProfile?.lastName) {
      return `${userProfile.firstName} ${userProfile.lastName}`;
    }
    if (userProfile?.firstName) {
      return userProfile.firstName;
    }
    return (
      currentUser?.displayName || currentUser?.email?.split("@")[0] || "User"
    );
  };

  const getUserEmail = () => {
    return currentUser?.email || "";
  };

  const getUserAvatar = () => {
    if (userProfile?.avatarPhoto) {
      return userProfile.avatarPhoto;
    }
    return currentUser?.photoURL || null;
  };

  const getInitials = () => {
    const name = getUserDisplayName();
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.charAt(0).toUpperCase();
  };

  const handleSignOut = async () => {
    try {
      analytics.trackCTAClick({
        section: "navigation",
        action: "sign_out",
        source: "user_menu",
      });
      await signOut();
      setIsOpen(false);
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const handleMenuItemClick = (path, action) => {
    if (action) {
      analytics.trackCTAClick({
        section: "navigation",
        action: action,
        source: "user_menu",
      });
    }
    history.push(path);
    setIsOpen(false);
  };

  if (!currentUser) {
    return null;
  }

  return (
    <div className="user-menu" ref={dropdownRef}>
      <button
        className="user-menu-trigger"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="User menu"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <div className="user-avatar">
          {getUserAvatar() ? (
            <img src={getUserAvatar()} alt="User avatar" />
          ) : (
            <div className="avatar-placeholder">{getInitials()}</div>
          )}
        </div>
        <span className="user-name-text">
          {loading ? "..." : getUserDisplayName()}
        </span>
        <span className="user-menu-arrow">{isOpen ? "▲" : "▼"}</span>
      </button>

      {isOpen && (
        <div className="user-menu-dropdown">
          <div className="user-menu-header">
            <div className="user-menu-avatar-large">
              {getUserAvatar() ? (
                <img src={getUserAvatar()} alt="User avatar" />
              ) : (
                <div className="avatar-placeholder-large">{getInitials()}</div>
              )}
            </div>
            <div className="user-menu-info">
              <div className="user-menu-name">{getUserDisplayName()}</div>
              <div className="user-menu-email">{getUserEmail()}</div>
            </div>
          </div>

          <div className="user-menu-divider"></div>

          <div className="user-menu-items">
            <button
              className="user-menu-item"
              onClick={() =>
                handleMenuItemClick("/profile", "navigate_to_profile")
              }
            >
              <span className="menu-item-icon">👤</span>
              <span>{t("navigation.profile", "Profile")}</span>
            </button>

            {/* Hide subscription option when in client view */}
            {!isClientView && (
              <button
                className="user-menu-item"
                onClick={() =>
                  handleMenuItemClick(
                    "/manage-subscription",
                    "navigate_to_subscription"
                  )
                }
              >
                <span className="menu-item-icon">💳</span>
                <span>{t("navigation.subscription", "Subscription")}</span>
              </button>
            )}

            <div className="user-menu-divider"></div>

            <button
              className="user-menu-item user-menu-item-signout"
              onClick={handleSignOut}
            >
              <span className="menu-item-icon">🚪</span>
              <span>{t("navigation.signOut", "Sign Out")}</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserMenu;
